/**
 * QuickAdd — Add items to cart by SKU + qty (cart sidebar)
 *
 * Two tabs: Quick Add | Import
 * Quick Add: enter item number + quantity, supports multiple rows via "Add another item"
 * Import: paste CSV or type item numbers (future: file upload)
 *
 * @module components/commerce/QuickAdd
 * @since 2026-05-25 (Group G)
 */
import { useState, useCallback } from 'react';
import { useCart } from '@contexts/CartContext';
import { findDemoProduct } from '@data/demoData';
import Toast from '@components/common/Toast';

export default function QuickAdd() {
  const { dispatch } = useCart();
  const [activeTab, setActiveTab] = useState('quick');
  const [toast, setToast] = useState(null);

  // Quick Add state — multiple rows
  const [rows, setRows] = useState([{ itemNumber: '', quantity: 1 }]);

  // Import state
  const [importText, setImportText] = useState('');

  const addRow = useCallback(() => {
    setRows(prev => [...prev, { itemNumber: '', quantity: 1 }]);
  }, []);

  const updateRow = useCallback((index, field, value) => {
    setRows(prev => prev.map((r, i) =>
      i === index ? { ...r, [field]: field === 'quantity' ? Math.max(1, parseInt(value) || 1) : value } : r
    ));
  }, []);

  const removeRow = useCallback((index) => {
    setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  }, []);

  const handleQuickAdd = useCallback(() => {
    const validRows = rows.filter(r => r.itemNumber.trim());
    if (validRows.length === 0) return;

    let added = 0;
    let notFound = [];

    validRows.forEach(r => {
      const product = findDemoProduct(r.itemNumber.trim());
      if (product) {
        dispatch({
          type: 'ADD_ITEM',
          payload: {
            productId: product.itemCode,
            itemCode: product.itemCode,
            description: product.description,
            unitPrice: product.unitPrice,
            weightKg: product.weightKg,
            weightLb: product.weightLb,
            bundledQty: product.bundledQty,
            shippingCost: product.shippingCost,
            productLine: product.productLine,
            category: product.category,
            availability: product.availability,
            estimatedDeliveryDays: product.estimatedDeliveryDays,
            quantity: r.quantity,
          },
        });
        added++;
      } else {
        notFound.push(r.itemNumber.trim());
      }
    });

    if (added > 0) {
      setToast({ type: 'success', message: `${added} item${added > 1 ? 's' : ''} added to cart` });
      setRows([{ itemNumber: '', quantity: 1 }]);
    }
    if (notFound.length > 0) {
      setToast({ type: 'error', message: `Not found: ${notFound.join(', ')}` });
    }
  }, [rows, dispatch]);

  const handleImport = useCallback(() => {
    const text = importText.trim();
    if (!text) return;

    // Parse CSV-like input: "itemCode,qty" per line
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    let added = 0;
    let notFound = [];

    lines.forEach(line => {
      const parts = line.split(/[,\t;]+/).map(s => s.trim());
      const itemCode = parts[0];
      const qty = parseInt(parts[1]) || 1;
      const product = findDemoProduct(itemCode);
      if (product) {
        dispatch({
          type: 'ADD_ITEM',
          payload: {
            productId: product.itemCode,
            itemCode: product.itemCode,
            description: product.description,
            unitPrice: product.unitPrice,
            weightKg: product.weightKg,
            weightLb: product.weightLb,
            bundledQty: product.bundledQty,
            shippingCost: product.shippingCost,
            productLine: product.productLine,
            category: product.category,
            availability: product.availability,
            estimatedDeliveryDays: product.estimatedDeliveryDays,
            quantity: qty,
          },
        });
        added++;
      } else {
        notFound.push(itemCode);
      }
    });

    if (added > 0) {
      setToast({ type: 'success', message: `${added} item${added > 1 ? 's' : ''} imported to cart` });
      setImportText('');
    }
    if (notFound.length > 0) {
      setToast({ type: 'error', message: `Not found: ${notFound.join(', ')}` });
    }
  }, [importText, dispatch]);

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-n2 mb-4">
        <nav className="flex gap-4" role="tablist">
          {[
            { key: 'quick', label: 'Quick add' },
            { key: 'import', label: 'Import' },
          ].map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative pb-2.5 text-sm font-normal transition-colors
                ${activeTab === tab.key
                  ? 'text-electric-blue'
                  : 'text-n5 hover:text-carbon-black'}`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'quick' ? (
        <div>
          {rows.map((row, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <div className="flex-1">
                {i === 0 && <label className="text-xs text-n5 mb-1 block">Enter item number</label>}
                <input
                  type="text"
                  value={row.itemNumber}
                  onChange={e => updateRow(i, 'itemNumber', e.target.value)}
                  placeholder="e.g. 7306-4772-01"
                  className="w-full border border-n3 px-3 py-2 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
                />
              </div>
              <div className="w-20">
                {i === 0 && <label className="text-xs text-n5 mb-1 block">Qty</label>}
                <input
                  type="number"
                  min="1"
                  value={row.quantity}
                  onChange={e => updateRow(i, 'quantity', e.target.value)}
                  className="w-full border border-n3 px-2 py-2 text-sm text-center focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
                />
              </div>
              {rows.length > 1 && (
                <button
                  onClick={() => removeRow(i)}
                  className="self-end pb-2 text-n4 hover:text-red-600"
                  aria-label="Remove row"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              )}
            </div>
          ))}

          <button
            onClick={addRow}
            className="text-sm text-electric-blue hover:underline flex items-center gap-1 mt-1 mb-4"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Add another item
          </button>

          <button
            onClick={handleQuickAdd}
            className="w-full bg-electric-blue text-white text-sm py-2.5 hover:bg-eb-3 transition-colors flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-base">add_shopping_cart</span>
            Add to cart
          </button>
        </div>
      ) : (
        <div>
          <label className="text-xs text-n5 mb-1 block">
            Paste item numbers (one per line, optional qty after comma)
          </label>
          <textarea
            value={importText}
            onChange={e => setImportText(e.target.value)}
            rows={6}
            placeholder={"7306-4772-01, 2\n7324-4724C-20, 10\n7314-3155, 4"}
            className="w-full border border-n3 px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-electric-blue focus:border-electric-blue resize-none"
          />
          <button
            onClick={handleImport}
            disabled={!importText.trim()}
            className={`w-full mt-3 text-sm py-2.5 transition-colors flex items-center justify-center gap-2
              ${importText.trim()
                ? 'bg-electric-blue text-white hover:bg-eb-3'
                : 'bg-n2 text-n4 cursor-not-allowed'}`}
          >
            <span className="material-symbols-outlined text-base">upload</span>
            Import to cart
          </button>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </div>
  );
}
